import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Users, Shield, Trash2, Loader2, CheckCircle, XCircle, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  admin: { label: "Admin", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400", desc: "Full access to everything" },
  manager: { label: "Manager", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400", desc: "Products, inventory, sales, reports" },
  cashier: { label: "Cashier", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400", desc: "Sales, read-only products" },
  stocker: { label: "Stocker", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400", desc: "Inventory, receiving, products" },
};

const STATUS_CONFIG = {
  approved: { label: "Approved", icon: CheckCircle, color: "text-emerald-500", badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" },
  pending: { label: "Pending", icon: Clock, color: "text-orange-500", badge: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" },
  suspended: { label: "Suspended", icon: XCircle, color: "text-red-500", badge: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" },
};

export default function UserManagement({ user: currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/api/users`, { credentials: "include" });
      if (res.status === 403) {
        toast.error("Admin access required");
        return;
      }
      if (!res.ok) throw new Error("Failed");
      setUsers(await res.json());
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleRoleChange = async (userId, newRole) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/users/${userId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail);
      }
      toast.success(`Role updated to ${newRole}`);
      fetchUsers();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (userId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/users/${userId}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail);
      }
      toast.success("User removed");
      setConfirmDelete(null);
      fetchUsers();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (userId, newStatus) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/users/${userId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail);
      }
      toast.success(`User ${newStatus === "approved" ? "approved" : newStatus}`);
      fetchUsers();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Layout user={currentUser}>
      <div className="space-y-6" data-testid="user-management-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            User Management
          </h1>
          <p className="text-muted-foreground">Manage team members and their permissions</p>
        </div>

        {/* Role Legend */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
            <Card key={key} className="rounded-sm">
              <CardContent className="p-3">
                <Badge className={`${cfg.color} mb-1`}>{cfg.label}</Badge>
                <p className="text-xs text-muted-foreground">{cfg.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Users Table */}
        <Card className="rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Team Members ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const isCurrentUser = u.user_id === currentUser?.user_id;
                    const cfg = ROLE_CONFIG[u.role] || ROLE_CONFIG.stocker;
                    const statusCfg = STATUS_CONFIG[u.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusCfg.icon;
                    return (
                      <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <span className="text-primary font-semibold text-sm">
                                  {u.name?.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{u.name}</p>
                              {isCurrentUser && <p className="text-xs text-muted-foreground">(You)</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{u.email}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${statusCfg.badge}`}>
                            <StatusIcon className="h-3 w-3" />
                            {statusCfg.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          {isCurrentUser ? (
                            <Badge className={cfg.color}>{cfg.label}</Badge>
                          ) : (
                            <Select
                              value={u.role}
                              onValueChange={(val) => handleRoleChange(u.user_id, val)}
                              disabled={saving || u.status !== "approved"}
                            >
                              <SelectTrigger className="w-32 h-8 rounded-sm" data-testid={`role-select-${u.user_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="manager">Manager</SelectItem>
                                <SelectItem value="cashier">Cashier</SelectItem>
                                <SelectItem value="stocker">Stocker</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-data">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell>
                          {!isCurrentUser && (
                            <div className="flex items-center gap-1">
                              {u.status === "pending" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStatusChange(u.user_id, "approved")}
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                                  disabled={saving}
                                  data-testid={`approve-user-${u.user_id}`}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              )}
                              {u.status === "approved" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStatusChange(u.user_id, "suspended")}
                                  className="text-orange-500 hover:text-orange-600"
                                  disabled={saving}
                                  data-testid={`suspend-user-${u.user_id}`}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                              {u.status === "suspended" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStatusChange(u.user_id, "approved")}
                                  className="text-emerald-600 hover:text-emerald-700"
                                  disabled={saving}
                                  data-testid={`reactivate-user-${u.user_id}`}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmDelete(u)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-user-${u.user_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation */}
        <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove User</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Remove <strong>{confirmDelete?.name}</strong> ({confirmDelete?.email}) from the system? This will revoke their access.
            </p>
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setConfirmDelete(null)} className="rounded-sm">Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(confirmDelete?.user_id)}
                disabled={saving}
                className="rounded-sm flex-1"
                data-testid="confirm-delete-user-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove User"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
